import React from 'react';
import styled from 'styled-components';
import IconBlock from '@component/common/block/icon-block';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme, TIcon, TIconSize } from '@datatype/type';

type TComponentSize = 'small' | 'medium' | 'large';
type TDirection = 'row' | 'column';

interface IProps {
  icon: TIcon;
  open?: boolean;
  theme?: ETheme;
  size?: TComponentSize;
  direction?: TDirection;
  gap?: string;
  width?: '100%' | 'fit-content' | string;
  height?: '100%' | 'auto' | string;
  handleClick?: () => void;
  children?: React.ReactNode;
}

const createDesign = (
  componentSize: TComponentSize | undefined,
  theme: ETheme | undefined,
  open: boolean,
  direction: TDirection | undefined,
  gap: string | undefined,
  width: string | undefined,
  height: string | undefined,
) => {
  // ############### createSize ###############
  const createSize = () => {
    switch (componentSize) {
      case 'small':
        return {
          buttonSize: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
          iconSize: 'tiny' as TIconSize,
        };
      case 'large':
        return {
          buttonSize: size.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT,
          iconSize: 'medium' as TIconSize,
        };
      case 'medium':
      default:
        return {
          buttonSize: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
          iconSize: 'small' as TIconSize,
        };
    }
  };

  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.CYAN:
        return { openBackgroundColor: color.CYAN_5, iconColor: color.BLACK_2 };
      case ETheme.GRAY:
        return { openBackgroundColor: color.GRAY_4, iconColor: color.BLACK_2 };
      case ETheme.BROWN:
      default:
        return { openBackgroundColor: color.BROWN_5, iconColor: color.BROWN_1 };
    }
  };

  // ############### createEvent ###############
  const createEvent = () => ({ cursor: 'pointer' as const });

  // ############### createCustom ###############
  const createCustom = () => ({
    direction,
    gap,
    backgroundColor: open ? createTheme().openBackgroundColor : color.WHITE_1,
    borderColor: color.GRAY_BOX_BORDER_COLOR,
    borderRadius: size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS,
    borderWidth: size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH,
    shadow: color.FLOATING_SHADOW,
  });

  const sizeDesign = createSize();
  const themeDesign = createTheme();

  return {
    width,
    height,
    ...sizeDesign,
    ...themeDesign,
    ...createCustom(),
    event: createEvent(),
  };
};

interface IWrapperProps {
  $design: ReturnType<typeof createDesign>;
}

const Wrapper = styled.div<IWrapperProps>`
  display: flex;
  width: ${(props) => props.$design.width || 'fit-content'};
  height: ${(props) => props.$design.height || 'auto'};
  flex-flow: ${(props) => props.$design.direction || 'column'} nowrap;
  align-items: ${(props) => (props.$design.direction === 'row' ? 'flex-end' : 'flex-start')};
  gap: ${(props) => props.$design.gap || '0px'};
`;

const FloatingControl: React.FC<IProps> = ({
  icon,
  open,
  theme,
  size: componentSize,
  direction,
  gap,
  width,
  height,
  handleClick,
  children,
}) => {
  const design = createDesign(componentSize, theme, !!open, direction, gap, width, height);

  return (
    <Wrapper $design={design}>
      <IconBlock
        icon={icon}
        design={{
          box: {
            width: design.buttonSize,
            height: design.buttonSize,
            backgroundColor: design.backgroundColor,
            border: true,
            borderWidth: design.borderWidth,
            borderColor: design.borderColor,
            borderRadius: design.borderRadius,
            shadow: design.shadow,
            event: design.event,
          },
          icon: { size: design.iconSize, kind: 'outlined', color: design.iconColor },
        }}
        click={{ handler: handleClick }}
      />
      {open && children}
    </Wrapper>
  );
};

export default FloatingControl;
